import moment from "moment";
import { toast } from "react-toastify";

import * as DatasetAPI from "api/DatasetAPI";

import * as loginReducer from "containers/Login/reducer";
import * as errorReducer from "containers/Errors/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import * as homeReducer from "containers/Home/reducer";
import * as projectReducer from "containers/Project/reducer";

import { updateIsDataUploading } from "containers/App/reducer";
import { transformUpdateDataset, getDataset, transformDataset, updateDataset } from "containers/Home/reducer";
import { getSelectedAccount } from "containers/Accounts/reducer";

export const UPDATE_FIELD = "upload/UPDATE_FIELD";
export const RESET = "upload/RESET";

const REINDEX_DATASET_TAG = "Dataset Reindex 2019-8-30";

const initialState = {
  isOpen: false,
  selection: undefined,
  label: "New Blueprint",
  header: undefined,
  outcome: undefined
};

function uploadReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.key]: action.data
      };
    case RESET:
      return {
        ...initialState
      };
    default:
      return state;
  }
}

/* Selectors */
export function getUpload(state) {
  return state.upload;
}

export function getIsOpen(state) {
  return getUpload(state).isOpen;
}

export function getHeader(state) {
  return getUpload(state).header;
}

export function getFileName(state) {
  return getUpload(state).fileName;
}

export function getOutcome(state) {
  return getUpload(state).outcome;
}

export function getDatasetId(state) {
  return getUpload(state).datasetId;
}

export function getDatasetRows(state) {
  return getUpload(state).datasetRows;
}

export function getDatasetHeaders(state) {
  return getUpload(state).datasetHeaders;
}

export function getClassifiers(state) {
  return getUpload(state).classifiers;
}

export function getClassifierName(state) {
  return getUpload(state).classifierName;
}

export function getCombinedDatsetId(state) {
  return getUpload(state).combinedDatasetId;
}

export function getCombinedProjectId(state) {
  return getUpload(state).combinedProjectId;
}

export function getIsImageOpen(state) {
  return getUpload(state).isImageOpen;
}

export function getModelOutcomeColumn(state) {
  return getUpload(state).modelOutcomeColumn;
}

export function getFabricDatasetId(state) {
  return getUpload(state).fabricDatasetId;
}

export function getUploadType(state) {
  return getUpload(state).uploadType;
}

export function getUploadEncoding(state) {
  return getUpload(state).uploadEncoding ?? getUploadEncodingDefault(state);
}

export function getUploadEncodingDefault(state) {
  return "UTF-8";
}

export function getUploadEncodingOptions(state) {
  const ENCODING_OPTIONS = [
    { label: "Auto Detect", value: "auto" },
    { label: "UTF-8", value: "UTF-8" },
    { label: "UTF-16", value: "UTF-16" },
    { label: "UTF-32", value: "UTF-32" },
    { label: "ISO-8859-1", value: "ISO-8859-1" },
    { label: "Windows-1252", value: "windows-1252" },
    { label: "ASCII", value: "ASCII" }
  ];

  return ENCODING_OPTIONS;
}

export function getUploadDelimiter(state) {
  return getUpload(state).uploadDelimiter;
}

export function getUploadDelimiterOptions(state) {
  const DELIMITER_OPTIONS = [
    { label: "Auto Detect", value: undefined },
    { label: ",", value: "," },
    { label: ";", value: ";" },
    { label: "|", value: "|" },
    { label: "Tab", value: "\t" }
  ];

  return DELIMITER_OPTIONS;
}

export function getUploadDateFormatString(state) {
  return getUpload(state).dateFormatString;
}

export function getUploadDateFormatStringOptions(state) {
  const FORMAT_STRING_OPTIONS = [
    { label: "ISO-8601", value: undefined },
    { label: "%Y-%m-%d %H:%M:%S %z", value: "%Y-%m-%d %H:%M:%S %z" },
    { label: "%m/%d/%y", value: "%m/%d/%y" }
  ];

  return FORMAT_STRING_OPTIONS;
}
export function getFabricDataset(state) {
  const fDatasetId = getFabricDatasetId(state);

  const dataset = homeReducer.getDataset(state, fDatasetId);

  return dataset;
}

export function getCombinedProject(state) {
  const projectId = getCombinedProjectId(state);
  return projectReducer.getProject(state, projectId);
}

/* Actions */
export function reset() {
  return { type: RESET };
}

export function updateHeader(header) {
  return { type: UPDATE_FIELD, data: header, key: "header" };
}

export function updateDatasetId(datasetId) {
  return { type: UPDATE_FIELD, data: datasetId, key: "datasetId" };
}

export function updateIsOpen(isOpen) {
  if (isOpen !== true) {
    const uploadInput = document.getElementById("fUpload");
    if (uploadInput) {
      uploadInput.value = "";
    }
  }

  return { type: UPDATE_FIELD, data: isOpen, key: "isOpen" };
}

export function updateUploadFileName(fileName) {
  return { type: UPDATE_FIELD, data: fileName, key: "fileName" };
}

export function handleChangeOutcome(outcome) {
  return { type: UPDATE_FIELD, data: outcome, key: "outcome" };
}

export function updateUploadType(data) {
  return { type: UPDATE_FIELD, data: data, key: "uploadType" };
}

export function updateUploadEncoding(data) {
  return { type: UPDATE_FIELD, data: data, key: "uploadEncoding" };
}

export function updateUploadDelimiter(data) {
  return { type: UPDATE_FIELD, data: data, key: "uploadDelimiter" };
}

export function updateUploadDateFormatString(data) {
  return { type: UPDATE_FIELD, data: data, key: "dateFormatString" };
}

/* Helpers */
// /^.([cC][sS][vV]$|[zZ][iI][pP]$|[gG][zZ]$)/
export function validateFileExtension(file, extension = /^.([cC][sS][vV]$|[gG][zZ]$)/) {
  const fileExtension = file.name.substr(file.name.lastIndexOf("."));
  if (
    !extension.test(fileExtension) &&
    fileExtension != ".parquet" &&
    fileExtension != ".arrow" &&
    fileExtension != ".jsonl" &&
    fileExtension != ".jsonl.gz" &&
    fileExtension != ".json" &&
    fileExtension != ".json.gz"
  ) {
    toast("Invalid File Type. Please upload a CSV or a gzip file.", {
      type: toast.TYPE.ERROR,
      position: toast.POSITION.TOP_CENTER,
      autoClose: 5000
    });
    return false;
  }
  return true;
}

export function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

export function handleFileChange(file, target, additionalOptions = {}, uploadOptions = {}) {
  return (dispatch, getState) => {
    const state = getState();
    if (!file) {
      const noFile = new Promise(function (resolve, reject) {
        resolve(false);
      });
      return noFile;
    }
    const isEvaluationAccount = loginReducer.getIsEvaluationAccount(state);
    if (isEvaluationAccount) {
      const selectedAccount = loginReducer.getSelectedTenant(state) || 0;
      const sizeLimit =
        selectedAccount && selectedAccount.limits && selectedAccount.limits.training_upload_size_bytes_limit;
      if (sizeLimit !== null && sizeLimit !== undefined && file.size >= sizeLimit) {
        dispatch(
          errorReducer.updateError(
            "You cannot upload a file larger than " + formatBytes(sizeLimit) + " on an evaluation account"
          )
        );

        if (target !== undefined) {
          target.value = "";
        }

        return new Promise(function (resolve, reject) {
          resolve(false);
        });
      }
    }

    const uploadType = getUploadType(state);
    const encoding = getUploadEncoding(state);
    const delimiter = getUploadDelimiter(state);
    const dateFormatString = getUploadDateFormatString(state);
    const readOptions = uploadOptions ?? {};

    if (uploadType !== undefined) {
      readOptions.type = uploadType;
    }
    if (encoding !== undefined || delimiter !== undefined) {
      if (encoding !== undefined) {
        readOptions.encoding = encoding;
      }
      if (delimiter !== undefined) {
        readOptions.delimiter = delimiter;
      }
      if (dateFormatString !== undefined) {
        readOptions.date_formats = [dateFormatString];
      }
    }

    const account = getSelectedAccount(state);
    dispatch(updateIsDataUploading(true));

    let folderId = foldersReducer.getSelectedFolder(state);
    return DatasetAPI.uploadDataset(file, {
      type: account.type,
      accountId: account.id,
      folderId: folderId,
      options: readOptions,
      ...additionalOptions
    })
      .then((response) => {
        if (target !== undefined) {
          target.value = "";
        }
        dispatch(updateIsDataUploading(false));

        const dataset = transformDataset(response);

        dispatch(updateDataset(response.id, dataset));

        return Promise.resolve(dataset);
      })
      .catch((e) => {
        if (target !== undefined) {
          target.value = "";
        }
        console.log(e);
        dispatch(updateIsDataUploading(false));
      });
  };
}

export function handleOpenUpload(isOpen, datasetId) {
  return (dispatch, getState) => {
    const dataset = getDataset(getState(), datasetId);

    if (dataset && dataset.fields) {
      const options = homeReducer.getOutcomeOptions(getState(), datasetId);

      dispatch(updateUploadFileName(dataset.label));
      dispatch(updateHeader(options));
    }

    dispatch(handleChangeOutcome(undefined));
    dispatch(updateDatasetId(datasetId));
    dispatch(updateIsOpen(true));

    return Promise.resolve();
  };
}

export function handleUpload(datasetId) {
  return (dispatch, getState) => {
    /*
		const file =
			document.getElementById("fUpload") && document.getElementById("fUpload").files
				? document.getElementById("fUpload").files[0]
				: undefined;

		const outcome = getOutcome(getState()) ? getOutcome(getState()).value : undefined;

		if (file !== undefined) {
			return DatasetAPI.uploadDataset(file, outcome).then(response => {
				dispatch(transformUpdateDataset(response.id, response));
				return dispatch(updateIsOpen(false));
			});
		}

		*/

    const state = getState();
    const retDatasetId = datasetId !== undefined ? datasetId : getDatasetId(state);
    const outcome = getOutcome(state) ? getOutcome(state).value : undefined;

    return DatasetAPI.setTarget(retDatasetId, outcome).then((response) => {
      dispatch(transformUpdateDataset(response.id, response));
      return dispatch(updateIsOpen(false));
    });
  };
}

export function reindexDataset(datasetId) {
  return (dispatch, getState) => {
    return DatasetAPI.reindex(datasetId).then((response) => {
      return dispatch(transformUpdateDataset(response.id, response));
    });
  };
}

// Checks if reindex is required (in ISO time with timezone!)
export function getDatasetNeedsReindex(data) {
  const isShell =
    data?.status_name == "NOT_STARTED" && data.labels.length == 0 && (data.stack_id ?? "").startsWith("00000000");

  return isShell || moment.utc(data.updated_date).isBefore(moment("2020-04-15T00:00:00-07:00"));
}
export default uploadReducer;
